<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useMediaQuery } from '@vueuse/core'
import { computed, onErrorCaptured, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import ConfirmLeaveDialog from '@/components/ConfirmLeaveDialog.vue'
import DevErrorOverlay from '@/components/DevErrorOverlay.vue'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useAuthStore } from '@/stores/auth'

const isDev = import.meta.env.DEV

const auth = useAuthStore()
const { t } = useI18n()

// §6-BIS.9: десктоп — снизу-справа, мобилка — сверху (не перекрывает таб-бар/FAB).
const isDesktop = useMediaQuery('(min-width: 640px)')
const toasterPosition = computed(() => (isDesktop.value ? 'bottom-right' : 'top-center'))

// Error boundary всего дерева: ошибка в любом дочернем компоненте (render,
// lifecycle, watcher) не уносит SPA в белый экран. Репортим в Sentry с фазой
// компонента (info — "render"/"setup"/...) и показываем ненавязчивый тост
// вместо краша. Возвращаем false — гасим дальнейшее всплытие к
// app.config.errorHandler (двойного репорта не будет), поэтому info прокидываем
// сами, как в entrypoints/application.ts.
onErrorCaptured((err, _instance, info) => {
  Sentry.captureException(err, { extra: { info } })
  toast.error(t('errors.unexpected.title'), {
    description: t('errors.unexpected.hint'),
  })
  return false
})

function hideSplash() {
  const splash = document.getElementById('splash')
  if (!splash) return
  splash.classList.add('hidden')
  setTimeout(() => splash.remove(), 400)
}

onMounted(async () => {
  // Минимум показа splash — чтобы при мгновенном fetchMe не было мигания.
  const minDelay = new Promise((r) => setTimeout(r, 1000))
  try {
    await Promise.all([auth.fetchMe(), minDelay])
  } finally {
    hideSplash()
  }
})
</script>

<template>
  <!-- Единый TooltipProvider на всё дерево — tooltip-примитивы (RelativeTime и пр.)
       требуют provider-предка (reka), локальных обёрток по компонентам не плодим. -->
  <TooltipProvider>
    <!-- Без mode="out-in" и без keep-alive — иначе белый экран на logout (enter /login
         не стартует). Стопка через page-transition-host, см. application.css. -->
    <router-view v-slot="{ Component, route }">
      <div class="page-transition-host min-h-screen">
        <Transition :name="(route.meta.transition as string) || 'page-slide-left'">
          <component :is="Component" :key="route.matched[0]?.path ?? route.path" />
        </Transition>
      </div>
    </router-view>
    <ConfirmLeaveDialog />
    <DevErrorOverlay v-if="isDev" />
    <Toaster :position="toasterPosition" />
  </TooltipProvider>
</template>
